import { type Prisma } from '@prisma/client';
import { Carousel } from 'flowbite-react';
import { type CustomFlowbiteTheme } from 'flowbite-react/types';
import { type ReactNode, useMemo } from 'react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { Arrow } from './Arrow';
import { FaceLayout } from './FaceLayout';
import { Testimonial } from './Testimonial';

const MAX_TEXT_LENGTH = 150;

const carouselTheme: CustomFlowbiteTheme['carousel'] = {
    control: {},
    indicators: {
        base: 'h-2 w-2 rounded-full transition-opacity ',
        active: {
            on: 'bg-crispybluedark-700',
            off: ' cursor-pointer bg-crispybluedark-700/50 hover:bg-crispyblue-700'
        }
    }
};

interface Props {
    testimonials?: Prisma.TestimonialGetPayload<
        { select: { company: true; name: true; id: true; text: true; rating: true } }
    >[];
    children?: ReactNode;
}
export function Hero({ testimonials, children }: Props) {
    const { t } = useEditableTranslation('hero');

    const rating = useMemo(() => {
        if (testimonials?.length === 0) {
            return 0;
        }
        return testimonials!.reduce((acc, curr) => acc + curr.rating, 0) / testimonials!.length;
    }, [ testimonials ]);

    return (
        <FaceLayout showVideoIfAvailable={true} rating={rating} ratingClass='md:hidden'>
            <div className='relative px-4 sm:px-6 lg:pr-0 lg:pb-8 hidden md:block'>
                <div className='hidden lg:absolute lg:-top-32 lg:right-[-100vw] lg:bottom-0 lg:left-[-100vw] lg:block lg:bg-slate-100' />
                {testimonials?.length && (
                    <>
                        <Carousel
                            slideInterval={8000}
                            pauseOnHover={true}
                            indicators={testimonials.length > 1}
                            theme={carouselTheme}
                            className='h-62'
                            leftControl={<div className='hidden' />}
                            rightControl={<div className='hidden' />}
                        >
                            {testimonials.map((item) => (
                                <Testimonial key={item.id} data={item} maxTextLength={MAX_TEXT_LENGTH} />
                            ))}
                        </Carousel>
                    </>
                )}
            </div>
            <div className='bg-white pt-10 md:pt-16 lg:bg-transparent lg:pt-8'>
                <div className='px-4 sm:px-6 md:max-w-3xl md:px-4 lg:px-0'>
                    <h1 className='font-display text-4xl sm:text-5xl font-extrabold text-slate-900 md:text-6xl text-center md:text-left'>
                        {t('title')}
                    </h1>
                    <p className='mt-12 text-3xl md:text-4xl text-slate-600 text-center md:text-left'>
                        {t('subTitle')}
                    </p>
                    {children}
                    <Arrow className='hidden lg:block absolute ml-40 h-80 w-80 mt-8' />
                </div>
            </div>
        </FaceLayout>
    );
}
